
import { defineComponent, provide, computed, onMounted, ref, Ref, watch } from "vue";
import CiContentHub from "@components/content_hubs/content-hub.vue";
import CiContentHubGeneric from "@components/content_hubs/content-hub-generic.vue";
import ScrollSnapSlider from "@components/base/scroll-snap-slider.vue";
import SliderSectionWrapper from "@components/base/slider-section-wrapper.vue";
import ContentHubNavMenu from "@components/content_hubs/navigation/content-hub-nav-menu.vue";
import CiRelatedContentHubs from "@components/content_hubs/related-content-hubs.vue";
import { useRoute } from "vue-router/composables";
import { apiService } from "@services/api.service";
import { useLoader } from "@composables/loader";
import { ciTrackingService } from "@services/ci-tracking.service";
import { MetaInfo } from "vue-meta";
import { AxiosError } from "axios";
import { ADS_INVENTORY } from "@models/constants/ads_inventory";
import { ROUTENAMES } from "@models/constants/routes";
import { alternateService } from "@services/alternate.service";

export default defineComponent({
  components: {
    CiContentHub,
    CiContentHubGeneric,
    CiRelatedContentHubs,
    ContentHubNavMenu,
    "ci-scroll-snap-slider": ScrollSnapSlider,
    "ci-slider-section-wrapper": SliderSectionWrapper,
  },
  beforeRouteEnter(_to, _from, next) {
    next((vm: any) => vm.getContentRoute(vm.$route.params?.pathMatch));
  },
  metaInfo(): MetaInfo {
    const metaAttributes = {
      title:
        this.contentRoute?.content_hub?.meta_title ||
        this.contentRoute?.content_hub?.title ||
        this.$t("ui.views.content_hubs.index.meta_tags.title", { tag_list: this.tagNames.join(",") }).toString(),
      meta_description:
        this.contentRoute?.content_hub?.meta_description ||
        this.$t("ui.views.content_hubs.index.meta_tags.description", { tag_list: this.tagNames.join(",") }).toString(),
      image: this.contentRoute?.content_hub?.hero_image?.url,
      robots: `${this.genericContent ? "noindex" : "index"}, follow`,
      alternates: this.contentRoute?.content_hub
        ? alternateService.fromAlternateMap(this.contentRoute?.content_hub?.alternate_urls)
        : [],
    };
    const metaInfo = {
      title: metaAttributes.title,
      link: metaAttributes.alternates,
      meta: [
        { vmid: "robots", name: "robots", content: metaAttributes.robots },
        { vmid: "og:title", property: "og:title", content: metaAttributes.title },
        { vmid: "og:description", property: "og:description", content: metaAttributes.meta_description },
        { vmid: "description", name: "description", content: metaAttributes.meta_description },
        { vmid: "twitter:title", name: "twitter:title", content: metaAttributes.title },
        { vmid: "twitter:description", name: "twitter:description", content: metaAttributes.meta_description },
      ],
    };
    if (metaAttributes.image) {
      metaInfo.meta.push({
        vmid: "og:image",
        property: "og:image",
        content: metaAttributes.image,
      });
    }
    return metaInfo;
  },
  setup() {
    const route = useRoute();
    const loader = useLoader();
    const slug = ref(route.params?.pathMatch);
    const initialized = ref(false);
    const tagNames: Ref<Array<string | undefined>> = ref([]);
    const breadCrumbs: Ref<Array<ContentHubs.BreadCrumb>> = ref([]);
    const contentRoute: Ref<Nullable<ContentHubs.ContentRoute>> = ref(null);
    const genericContent = computed((): boolean => contentRoute.value?.content_hub === null);

    let articlesPage = 1;
    const articles: Ref<Articles.TileData[]> = ref([]);
    const articlesRemained = ref(false);

    let productsPage = 1;
    const products: Ref<Products.TileData[]> = ref([]);
    const productsRemained = ref(true);

    const rootRoute = computed(() => route.name === ROUTENAMES.CONTENT_HUBS_ROOT_SHOW);
    const adIds = computed(
      (): Record<string, string> => ({
        header: ADS_INVENTORY.content_hubs_header_slot.dom_id,
        skyscraper: ADS_INVENTORY.content_hubs_sidebar_top.dom_id,
        sidebar_2nd: ADS_INVENTORY.content_hubs_sidebar_2nd.dom_id,
      })
    );

    watch(
      () => route.params.pathMatch,
      async (newSlug) => {
        await refreshComponentData(newSlug);
      }
    );

    const refreshComponentData = (newSlug: string) => {
      slug.value = newSlug;
      articlesPage = 1;
      articles.value = [];
      articlesRemained.value = true;
      productsPage = 1;
      products.value = [];
      productsRemained.value = true;
      getContentRoute(slug.value);
      getProducts();
      getArticles();
    };

    const getContentRoute = async (slug: string): Promise<void> => {
      loader.start();

      apiService.contentHubs
        .getContentRoute(slug)
        .then((response: any) => {
          contentRoute.value = response.data;
          breadCrumbs.value = contentRoute.value!.breadcrumbs;
          tagNames.value = [
            contentRoute.value!.tag_level_1?.name,
            contentRoute.value!.tag_level_2?.name,
            contentRoute.value!.tag_level_3?.name,
            contentRoute.value!.tag_level_4?.name,
            contentRoute.value!.tag_level_5?.name,
            contentRoute.value!.tag_level_6?.name,
          ].filter((n) => n);
          initialized.value = true;
          return Promise.resolve(response.data);
        })
        .then(() => {
          ciTrackingService.trackView(
            genericContent.value ? "ContentHubGeneric" : "ContentHub",
            contentRoute.value?.content_hub?.id.toString()
          );
        })
        .catch((err) => {
          apiService.handleRoutingAPIError(err as AxiosError, (routerError) => loader.setError(routerError));
        })
        .finally(loader.stop);
    };
    const getArticles = async (): Promise<void> => {
      loader.start();
      apiService.contentHubs
        .getArticles(slug.value, articlesPage)
        .then((response) => {
          articles.value.push(...response.data.articles);
          articlesRemained.value = response.data.meta.next_page !== null;
          return Promise.resolve(response.data.meta);
        })
        .then((meta) => (articlesPage = meta.next_page || 0))
        .catch(apiService.handleAPIError)
        .finally(loader.stop);
    };
    const getProducts = async (): Promise<void> => {
      if (!productsRemained.value) return;

      loader.start();
      apiService.contentHubs
        .getProducts(slug.value, productsPage)
        .then((response) => {
          products.value.push(...response.data.products);
          productsRemained.value = response.data.meta.next_page !== null;
          return Promise.resolve(response.data.meta);
        })
        .then((meta) => (productsPage = meta.next_page || 1))
        .catch(apiService.handleAPIError)
        .finally(loader.stop);
    };

    onMounted(() => {
      getProducts();
      getArticles();
    });

    provide("breadCrumbs", breadCrumbs);
    provide("tagNames", tagNames);
    provide(
      "contentRoute",
      computed(() => contentRoute.value)
    );
    return {
      adIds,
      contentRoute,
      genericContent,
      products,
      articles,
      productsRemained,
      articlesRemained,
      getProducts,
      getArticles,
      getContentRoute,
      tagNames,
      breadCrumbs,
      initialized,
      rootRoute,
      refreshComponentData,
    };
  },
});
